import React from 'react';
import { SearchSelect } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/components/SearchSelect';
import { useAppSelector } from 'src/utils/Hooks';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { getFieldConfig } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants/formConfigs';
import { FormValue } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';

interface CategorySelectProps {
  selectionOrder: 'category' | 'subcategory' | null;
  onSelectionChange: (newValue: FormValue[]) => void;
  selectedSubcategoryIds: FormValue[];
}

export const CategorySelect = ({ selectionOrder, onSelectionChange, selectedSubcategoryIds }: CategorySelectProps) => {
  const {
    selections: { control },
    currentFormConfig
  } = usePresentationFormContext();
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);
  
  // Get field configuration
  const categoryConfig = getFieldConfig(currentFormConfig, 'categoryIds');

  const selectOptions = React.useMemo(() => {
    let filteredCategories = categories.filter(category => category.id !== 0);

    if (selectionOrder === 'subcategory' && selectedSubcategoryIds.length > 0) {
      const selectedSubcats = subCategories.filter(subcat =>
        selectedSubcategoryIds.some(selected => selected.id === subcat.id.toString())
      );

      const parentCategoryIds = new Set(
        selectedSubcats.map(subcat => subcat.parentCategoryId)
      );

      filteredCategories = filteredCategories.filter(category =>
        parentCategoryIds.has(category.id)
      );
    }

    return filteredCategories.map((category) => ({
      id: category.id,
      value: category.id.toString(),
      displayName: category.categoryName
    }));
  }, [categories, subCategories, selectionOrder, selectedSubcategoryIds]);

  // Build dynamic placeholder based on config
  const getPlaceholder = () => {
    const { minCount, maxCount } = categoryConfig;
    const hasMin = minCount !== undefined && minCount > 0;
    const hasMax = maxCount !== undefined;
    
    if (hasMin && hasMax) {
      if (minCount === maxCount) {
        return `Select exactly ${minCount} ${minCount === 1 ? 'category' : 'categories'}`;
      }
      return `Select ${minCount} to ${maxCount} categories`;
    }
    if (hasMin) {
      return `Select at least ${minCount} ${minCount === 1 ? 'category' : 'categories'}`;
    }
    if (hasMax) {
      return `Select up to ${maxCount} ${maxCount === 1 ? 'category' : 'categories'}`;
    }
    return 'Search or select categories you would like to add to your presentation';
  };

  return (
    <SearchSelect
      name="categoryIds"
      title="Category Inclusions"
      placeholder={getPlaceholder()}
      selectOptions={selectOptions}
      isMulti={true}
      maxCount={categoryConfig.maxCount}
      control={control}
      onSelectionChange={onSelectionChange}
    />
  );
};
